import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { FormProvider, useForm } from 'react-hook-form'
import { Button } from '@dashboard/ui'
import { ToastSeverity, useToast } from '@/ui'
import { getErrorText } from '@/api'
import { PageRead, Website } from '@/api/dashboard'
import { usePageSeoMutate } from '@/layouts/hub-settings/api/usePageSeoMutate'
import { HubSettingsPageFields } from '@/layouts/hub-settings/widgets/HubSettingsPageFields'
import { FormLayout } from '@/layouts/company/components/FormLayout'
import { useCurrentUser } from '@/api/useCurrentUser'

interface HubSettingsPageFormProps {
  website: Website
  page: PageRead
}

export const HubSettingsPageForm = ({ website, page }: HubSettingsPageFormProps) => {
  const { t } = useTranslation()
  const { canEdit: canUserEdit } = useCurrentUser()
  const showToast = useToast()
  const { mutateAsync: updateSeo } = usePageSeoMutate(page.id)
  const methods = useForm<PageRead>({ defaultValues: { ...page } })
  const {
    reset,
    handleSubmit,
    formState: { isDirty, isSubmitting },
  } = methods

  useEffect(() => {
    reset({ ...page })
  }, [page, reset])

  const onSubmit = handleSubmit(async data => {
    const mutations: Promise<unknown>[] = []

    canUserEdit &&
      data.seo &&
      mutations.push(
        updateSeo({
          update: {
            title: data.seo.title || null,
            description: data.seo.description || null,
            image_url: data.seo.image_url || null,
          },
        }),
      )

    return Promise.all(mutations).catch(e =>
      showToast({
        message: getErrorText(e),
        severity: ToastSeverity.error,
      }),
    )
  })

  return (
    <FormProvider {...methods}>
      <FormLayout
        onSubmit={onSubmit}
        footer={
          canUserEdit && (
            <>
              {isDirty && (
                <Button size="sm" variant="outline" color="secondary" disabled={isSubmitting} onClick={() => reset()}>
                  {t('Discard')}
                </Button>
              )}
              <Button type="submit" size="sm" loading={isSubmitting} disabled={!isDirty}>
                {t('Save')}
              </Button>
            </>
          )
        }
      >
        <HubSettingsPageFields website={website} page={page} />
      </FormLayout>
    </FormProvider>
  )
}
